// Origination.jsx — wizard-style assessment console for onboarding / appraisal / renewal / DD
import React, { useEffect, useState } from "react";
import {
  Box, Paper, Button, TextField, MenuItem, CircularProgress, Stepper, Step, StepLabel,
  Chip, Autocomplete, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
} from "@mui/material";
import {
  ClipboardList, MapPin, ChevronLeft, ChevronRight, Save, PlayCircle, Eye, Plus,
} from "lucide-react";
import { toast } from "react-toastify";
import DashboardLayout from "../layouts/DashboardLayout";
import RiskPill from "../components/RiskPill";
import {
  originationApi, borrowersApi, collateralsApi, formatApiError,
} from "../api/api";
import {
  fmtINR, fmtScore, fmtBps, fmtDateTime, HAZARD_LABELS,
} from "../utils/format";

const STAGE_LABELS = {
  onboarding: "Onboarding",
  credit_appraisal: "Credit Appraisal",
  renewal: "Renewal",
  investment_dd: "Investment Due Diligence",
};

export default function Origination() {
  const [templates, setTemplates] = useState([]);
  const [borrowerList, setBorrowerList] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [open, setOpen] = useState(false);
  const [tpl, setTpl] = useState(null);   // selected template
  const [step, setStep] = useState(0);
  const [borrowerId, setBorrowerId] = useState("");
  const [borrowerName, setBorrowerName] = useState("");
  const [values, setValues] = useState({});  // field values
  const [latlon, setLatlon] = useState({ lat: "", lon: "", pin: "" });
  const [valuation, setValuation] = useState("");
  const [resolving, setResolving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [viewAssessment, setViewAssessment] = useState(null);

  const load = async () => {
    const [t, b, a] = await Promise.all([
      originationApi.listTemplates(),
      borrowersApi.list(),
      originationApi.listAssessments(),
    ]);
    setTemplates(t.data.templates || []);
    setBorrowerList(b.data.borrowers || []);
    setAssessments(a.data.assessments || []);
  };
  useEffect(() => { load(); }, []);

  const start = (template) => {
    setTpl(template);
    setStep(0);
    setBorrowerId("");
    setBorrowerName("");
    setValues({});
    setLatlon({ lat: "", lon: "", pin: "" });
    setValuation("");
    setResult(null);
    setOpen(true);
  };

  const resolvePin = async () => {
    const p = latlon.pin?.trim();
    if (!/^\d{6}$/.test(p)) { toast.warn("Enter 6-digit PIN"); return; }
    setResolving(true);
    try {
      const { data } = await collateralsApi.geocode(p);
      setLatlon({ pin: p, lat: data.latitude.toFixed(6), lon: data.longitude.toFixed(6) });
      // also set in values map
      setValues((v) => ({
        ...v,
        primary_location_pin: p,
        registered_office_pin: p,
        collateral_pin: p,
        premises_pin: p,
      }));
      toast.success(`PIN ${p} resolved`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setResolving(false); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        template_id: tpl.template_id,
        borrower_id: borrowerId || null,
        borrower_name: borrowerName,
        portfolio: tpl.portfolio,
        stage: tpl.stage,
        values,
        latitude: latlon.lat ? Number(latlon.lat) : null,
        longitude: latlon.lon ? Number(latlon.lon) : null,
        pin_code: latlon.pin,
        current_valuation_inr: valuation ? Number(valuation) : 0,
      };
      const { data } = await originationApi.createAssessment(payload);
      setResult(data.assessment);
      setStep(tpl.sections.length); // jump to results
      toast.success("Assessment computed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const renderField = (f) => {
    const v = values[f.key] || "";
    const set = (val) => setValues({ ...values, [f.key]: val });
    if (f.type === "boolean") {
      return (
        <TextField key={f.key} size="small" select label={f.label} value={v}
          onChange={(e) => set(e.target.value)}
          required={f.required}>
          <MenuItem value="">—</MenuItem>
          <MenuItem value="yes">Yes</MenuItem>
          <MenuItem value="no">No</MenuItem>
        </TextField>
      );
    }
    if (f.type === "select" && f.options) {
      return (
        <TextField key={f.key} size="small" select label={f.label} value={v}
          onChange={(e) => set(e.target.value)} required={f.required}>
          {f.options.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
      );
    }
    if (f.type === "textarea") {
      return (
        <TextField key={f.key} size="small" multiline minRows={2} label={f.label} value={v}
          onChange={(e) => set(e.target.value)} sx={{ gridColumn: "1 / -1" }} />
      );
    }
    if (f.type === "latlon") {
      return null; // handled in geo section
    }
    if (f.type === "date") {
      return (
        <TextField key={f.key} size="small" type="date" label={f.label} value={v}
          onChange={(e) => set(e.target.value)} InputLabelProps={{ shrink: true }} />
      );
    }
    return (
      <TextField key={f.key} size="small" type={f.type === "number" ? "number" : "text"}
        label={f.label} value={v}
        onChange={(e) => set(e.target.value)}
        required={f.required} />
    );
  };

  const sections = tpl?.sections || [];

  return (
    <DashboardLayout
      title="Origination Console"
      subtitle="ONBOARDING · APPRAISAL · RENEWAL · DUE DILIGENCE"
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        Pick a template below to run a guided climate-risk assessment. Each assessment captures geospatial,
        valuation and physical-risk data — and runs the live scoring engine on submission.
      </Alert>

      {/* Templates grid */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }, gap: 2, mb: 3 }} data-testid="templates-grid">
        {templates.map((t) => (
          <Paper key={t.template_id}
            sx={{
              p: 2.4, cursor: "pointer", borderLeft: "3px solid #2E5A35",
              transition: "all 0.18s ease",
              "&:hover": { borderColor: "#1E2A38", transform: "translateY(-2px)" },
            }}
            onClick={() => start(t)}
            data-testid={`tpl-${t.template_id}`}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Chip size="small" label={STAGE_LABELS[t.stage] || t.stage}
                sx={{ fontSize: 10, background: "#F0EFEA", fontFamily: "IBM Plex Mono, monospace" }} />
              <Chip size="small" label={t.portfolio}
                sx={{ fontSize: 10, fontFamily: "IBM Plex Mono, monospace" }} />
            </Box>
            <Box className="cb-display" sx={{ fontSize: 16, fontWeight: 600, color: "#111827", mb: 0.5 }}>
              {t.name}
            </Box>
            <Box sx={{ fontSize: 12.5, color: "#4B5563", lineHeight: 1.5 }}>{t.description}</Box>
            <Box sx={{ mt: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11.5 }}>
              <Box className="cb-mono" sx={{ color: "#6B7280" }}>{t.sections?.length || 0} sections</Box>
              <Button size="small" variant="outlined" startIcon={<PlayCircle size={12} />}>Run</Button>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Past assessments */}
      <Paper sx={{ overflow: "hidden" }} data-testid="assessments-list">
        <Box sx={{ p: 2, borderBottom: "1px solid #F0EFEA" }}>
          <Box className="cb-display" sx={{ fontSize: 16, fontWeight: 600, color: "#111827" }}>
            Recent Assessments · {assessments.length}
          </Box>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Stage / Template</TableCell>
              <TableCell>Borrower</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell align="right">Score</TableCell>
              <TableCell align="right">PD Uplift</TableCell>
              <TableCell align="right">Exposure</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assessments.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: "#94A3B8" }}>
                No assessments yet — click a template above to start.
              </TableCell></TableRow>
            )}
            {assessments.slice(0, 50).map((a) => {
              const r = a.risk_assessment;
              return (
                <TableRow key={a.assessment_id} hover>
                  <TableCell sx={{ fontSize: 11.5 }}>{fmtDateTime(a.created_at)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={STAGE_LABELS[a.stage] || a.stage} sx={{ fontSize: 10, mr: 0.5 }} />
                    <Box sx={{ fontSize: 12, color: "#4B5563", display: "inline" }}>{a.template_name}</Box>
                  </TableCell>
                  <TableCell>{a.borrower_name || "—"}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{a.owner_email}</TableCell>
                  <TableCell align="right" className="cb-mono" sx={{ fontWeight: 600 }}>{r ? fmtScore(r.overall_score) : "—"}</TableCell>
                  <TableCell align="right" className="cb-mono">{r ? fmtBps(r.pd_uplift_bps) : "—"}</TableCell>
                  <TableCell align="right" className="cb-mono">{r ? fmtINR(r.financial_exposure_inr) : "—"}</TableCell>
                  <TableCell>
                    {r && <RiskPill level={r.risk_level} />}
                    <Tooltip title="View">
                      <IconButton size="small" onClick={() => setViewAssessment(a)} data-testid={`view-assessment-${a.assessment_id}`}>
                        <Eye size={13} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {/* Wizard dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth
        slotProps={{ paper: { sx: { minHeight: 540 } } }}>
        <DialogTitle sx={{ fontFamily: "Playfair Display, serif", fontWeight: 600, display: "flex", justifyContent: "space-between" }}>
          {tpl?.name}
          {tpl && <Chip size="small" label={STAGE_LABELS[tpl.stage] || tpl.stage} />}
        </DialogTitle>
        <DialogContent dividers>
          {tpl && (
            <>
              <Stepper activeStep={step} sx={{ mb: 3 }}>
                {sections.map((s) => <Step key={s.id}><StepLabel>{s.title}</StepLabel></Step>)}
                <Step><StepLabel>Results</StepLabel></Step>
              </Stepper>

              {/* Borrower selection on step 0 */}
              {step === 0 && (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.6, mb: 2.4 }}>
                  <Autocomplete
                    size="small" options={borrowerList} getOptionLabel={(o) => `${o.cif_no} · ${o.name}`}
                    value={borrowerList.find((b) => b.borrower_id === borrowerId) || null}
                    onChange={(_, v) => { setBorrowerId(v?.borrower_id || ""); setBorrowerName(v?.name || ""); }}
                    renderInput={(p) => <TextField {...p} label="Borrower (link existing)" inputProps={{ ...p.inputProps, "data-testid": "orig-borrower-select" }} />}
                  />
                  <TextField size="small" label="Or borrower name (free-text)" value={borrowerName}
                    onChange={(e) => setBorrowerName(e.target.value)}
                    inputProps={{ "data-testid": "orig-borrower-name" }}
                    helperText="Use for new prospects not yet onboarded." />
                </Box>
              )}

              {/* Render current section */}
              {step < sections.length && (
                <Box>
                  <Box className="cb-display" sx={{ fontSize: 17, fontWeight: 600, color: "#111827", mb: 0.5 }}>
                    {sections[step].title}
                  </Box>
                  {/* If section contains a latlon field, render PIN resolver */}
                  {sections[step].fields.some((f) => f.type === "latlon") && (
                    <Paper sx={{ p: 1.6, mb: 2, background: "#FBFBFA", border: "1px dashed #CBD5E1" }}>
                      <Box sx={{ fontSize: 11.5, color: "#4B5563", mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                        <MapPin size={13} /> Geo-location capture
                      </Box>
                      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr auto" }, gap: 1 }}>
                        <TextField size="small" label="PIN code" value={latlon.pin}
                          onChange={(e) => setLatlon({ ...latlon, pin: e.target.value })}
                          inputProps={{ "data-testid": "orig-pin", maxLength: 6 }} />
                        <TextField size="small" label="Latitude" value={latlon.lat}
                          onChange={(e) => setLatlon({ ...latlon, lat: e.target.value })}
                          inputProps={{ "data-testid": "orig-lat" }} />
                        <TextField size="small" label="Longitude" value={latlon.lon}
                          onChange={(e) => setLatlon({ ...latlon, lon: e.target.value })}
                          inputProps={{ "data-testid": "orig-lon" }} />
                        <Button onClick={resolvePin} disabled={resolving} variant="outlined" data-testid="orig-resolve-pin">
                          {resolving ? <CircularProgress size={14} /> : "Resolve"}
                        </Button>
                      </Box>
                    </Paper>
                  )}
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.6 }}>
                    {sections[step].fields.map(renderField)}
                  </Box>
                  {/* On the last data section, allow capturing valuation if not in fields */}
                  {step === sections.length - 1 && !sections[step].fields.some((f) => f.key.includes("valuation") || f.key === "exposure_inr") && (
                    <TextField size="small" type="number" sx={{ mt: 2, width: "100%", maxWidth: 360 }}
                      label="Collateral / exposure valuation (INR)" value={valuation}
                      onChange={(e) => setValuation(e.target.value)}
                      inputProps={{ "data-testid": "orig-valuation" }} />
                  )}
                </Box>
              )}

              {/* Results step */}
              {step === sections.length && (
                <Box data-testid="orig-result">
                  <Box className="cb-display" sx={{ fontSize: 18, fontWeight: 600, color: "#111827", mb: 1 }}>
                    Climate-Risk Snapshot
                  </Box>
                  {result?.risk_assessment ? (
                    <>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.6, mb: 2 }}>
                        <Paper sx={{ p: 1.6 }}>
                          <Box className="cb-kpi-label">Overall Score</Box>
                          <Box className="cb-mono" sx={{ fontSize: 26, fontWeight: 600 }}>{fmtScore(result.risk_assessment.overall_score)}</Box>
                          <RiskPill level={result.risk_assessment.risk_level} />
                        </Paper>
                        <Paper sx={{ p: 1.6 }}>
                          <Box className="cb-kpi-label">Financial Exposure</Box>
                          <Box className="cb-mono" sx={{ fontSize: 18, fontWeight: 600 }}>{fmtINR(result.risk_assessment.financial_exposure_inr)}</Box>
                        </Paper>
                        <Paper sx={{ p: 1.6 }}>
                          <Box className="cb-kpi-label">PD Uplift</Box>
                          <Box className="cb-mono" sx={{ fontSize: 18, fontWeight: 600, color: "#8F5E14" }}>{fmtBps(result.risk_assessment.pd_uplift_bps)}</Box>
                        </Paper>
                      </Box>
                      <Box sx={{ fontSize: 13, color: "#4B5563", mb: 1, fontWeight: 600 }}>Top hazards driving risk</Box>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
                        {(result.risk_assessment.hazards || [])
                          .filter((h) => h.overall_score >= 30)
                          .slice(0, 6)
                          .map((h) => (
                            <Chip key={h.hazard_id} size="small"
                              label={`${HAZARD_LABELS[h.hazard_id] || h.hazard_id} · ${fmtScore(h.overall_score)}`}
                              sx={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 11 }} />
                          ))}
                      </Box>
                      {/* Top recommendation */}
                      {(() => {
                        const top = (result.risk_assessment.hazards || []).find((h) => (h.recommendations || []).length > 0);
                        if (!top) return null;
                        return (
                          <Box sx={{ mt: 2, p: 1.6, background: "#FBFBFA", border: "1px solid #E2E8F0", borderRadius: 1 }}>
                            <Box className="cb-kpi-label">Recommendations · {HAZARD_LABELS[top.hazard_id]}</Box>
                            <Box component="ul" sx={{ m: 0, pl: 2.4, fontSize: 12.5, color: "#4B5563" }}>
                              {top.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                            </Box>
                          </Box>
                        );
                      })()}
                    </>
                  ) : <Alert severity="warning">No physical-risk computation — was lat/lon captured?</Alert>}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
          {step > 0 && step <= sections.length && (
            <Button onClick={() => setStep((s) => s - 1)} startIcon={<ChevronLeft size={13} />}>Back</Button>
          )}
          {step < sections.length - 1 && (
            <Button variant="contained" onClick={() => setStep((s) => s + 1)} endIcon={<ChevronRight size={13} />}
              data-testid="orig-next-btn"
              sx={{ background: "#1E2A38", "&:hover": { background: "#0F1721" } }}>
              Next
            </Button>
          )}
          {step === sections.length - 1 && (
            <Button variant="contained" onClick={submit} disabled={submitting}
              startIcon={submitting ? <CircularProgress size={12} sx={{ color: "#FFF" }} /> : <Save size={13} />}
              data-testid="orig-submit-btn"
              sx={{ background: "#2E5A35", "&:hover": { background: "#1f4023" } }}>
              {submitting ? "Computing…" : "Submit & score"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* View assessment dialog */}
      <Dialog open={Boolean(viewAssessment)} onClose={() => setViewAssessment(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: "Playfair Display, serif", fontWeight: 600 }}>
          Assessment · {viewAssessment?.template_name}
        </DialogTitle>
        <DialogContent dividers>
          {viewAssessment && (() => {
            const r = viewAssessment.risk_assessment;
            return (
              <>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1.4, mb: 2 }}>
                  <Box><Box className="cb-kpi-label">Borrower</Box><Box>{viewAssessment.borrower_name || "—"}</Box></Box>
                  <Box><Box className="cb-kpi-label">Created</Box><Box>{fmtDateTime(viewAssessment.created_at)}</Box></Box>
                  <Box><Box className="cb-kpi-label">Stage</Box><Box>{STAGE_LABELS[viewAssessment.stage] || viewAssessment.stage}</Box></Box>
                  <Box><Box className="cb-kpi-label">Portfolio</Box><Box sx={{ textTransform: "capitalize" }}>{viewAssessment.portfolio}</Box></Box>
                </Box>
                {r && (
                  <Box sx={{ p: 2, background: "#FBFBFA", borderRadius: 1, border: "1px solid #E2E8F0", mb: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.2, alignItems: "center" }}>
                      <Box className="cb-display" sx={{ fontSize: 15, fontWeight: 600 }}>Climate risk snapshot</Box>
                      <RiskPill level={r.risk_level} />
                    </Box>
                    <Box className="cb-mono" sx={{ fontSize: 13 }}>
                      Score {fmtScore(r.overall_score)} · {fmtINR(r.financial_exposure_inr)} · {fmtBps(r.pd_uplift_bps)} PD uplift
                    </Box>
                    {r.top_hazard && (
                      <Box className="cb-mono" sx={{ fontSize: 11.5, color: "#6B7280", mt: 0.5 }}>
                        Top hazard: {HAZARD_LABELS[r.top_hazard] || r.top_hazard}
                      </Box>
                    )}
                  </Box>
                )}
                <Box className="cb-kpi-label" sx={{ mb: 1 }}>Captured values</Box>
                <Table size="small">
                  <TableBody>
                    {Object.entries(viewAssessment.values || {}).map(([k, v]) => (
                      <TableRow key={k}>
                        <TableCell sx={{ fontWeight: 600, width: "40%" }}>{k.replace(/_/g, " ")}</TableCell>
                        <TableCell className="cb-mono" sx={{ fontSize: 12.5 }}>{String(v ?? "—")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewAssessment(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
